import type { AuthMe } from '@entailor/auth-kit-browser';

import { clearActiveOrgState, getActiveOrgId, setActiveOrgState } from 'auth/activeOrg';

const SESSIONS_KEY = 'tailor-sessions';
const LAST_ACTIVE_ORG_KEY = 'tailor-last-active-org';

// A cache of the /_auth/me org list. The opaque __Host-Session cookie is the real
// credential; this only lets the header and the org resolver answer synchronously.

export interface StoredSession {
  orgId: string;
  orgSlug: string;
  userId: string;
  userName: string;
  userEmail: string;
}

// What /_auth/me returned on this page load. Memory, not localStorage, is the
// source of truth here: AuthGuard writes the org list and reads it straight back,
// so a browser that refuses to persist (private mode, blocked storage) would
// otherwise read an empty store and bounce an authenticated user to login —
// which returns them here, to fail the same way. localStorage only carries the
// list to the *next* load; losing it there really is just a re-fetch.
let cache: Record<string, StoredSession> | null = null;

function readStore(): Record<string, StoredSession> {
  if (cache) return cache;
  try {
    const raw = localStorage.getItem(SESSIONS_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as Record<string, StoredSession>;
  } catch {
    return {};
  }
}

function writeStore(store: Record<string, StoredSession>): void {
  cache = store;
  try {
    localStorage.setItem(SESSIONS_KEY, JSON.stringify(store));
  } catch {
    // Storage unavailable (private mode, blocked cookies) — the cookie is the
    // real credential and `cache` holds the list for this page load.
  }
}

export function getSessions(): StoredSession[] {
  return Object.values(readStore());
}

export function getSession(orgId: string): StoredSession | null {
  return readStore()[orgId] ?? null;
}

export function getSessionBySlug(slug: string): StoredSession | null {
  return getSessions().find((s) => s.orgSlug === slug) ?? null;
}

let defaultOrgSlug: string | null = null;

export function replaceSessions(me: AuthMe): void {
  defaultOrgSlug = me.defaultOrg;
  const store: Record<string, StoredSession> = {};
  for (const o of me.orgs) {
    store[o.orgId] = {
      orgId: o.orgId,
      orgSlug: o.orgSlug,
      userId: o.userId,
      userName: o.userName,
      userEmail: o.userEmail,
    };
  }
  writeStore(store);
}

export function clearAllSessions(): void {
  // Empty, not null: cleared has to win over whatever localStorage still holds.
  cache = {};
  try {
    localStorage.removeItem(SESSIONS_KEY);
  } catch {
    // Storage unavailable (private mode, blocked cookies) — the cookie is the
    // real credential, so a lost cache is a re-fetch, not a failure.
  }
}

export function setActiveOrg(slug: string, orgId: string): void {
  setActiveOrgState(slug, orgId);
  try {
    localStorage.setItem(LAST_ACTIVE_ORG_KEY, slug);
  } catch {
    // Storage unavailable (private mode, blocked cookies) — the cookie is the
    // real credential, so a lost cache is a re-fetch, not a failure.
  }
}

export function clearActiveOrg(): void {
  clearActiveOrgState();
  try {
    localStorage.removeItem(LAST_ACTIVE_ORG_KEY);
  } catch {
    // Storage unavailable (private mode, blocked cookies) — the cookie is the
    // real credential, so a lost cache is a re-fetch, not a failure.
  }
}

// The org a URL without an /o/<slug> prefix should land on: the post-login or
// post-switch ?_org marker, then the last org used in this browser, then the
// session default, then any org. Requires replaceSessions() to have run.
export function resolveLandingOrgSlug(orgHint: string | null): string | null {
  const sessions = getSessions();
  const known = (slug: string | null): slug is string => slug != null && sessions.some((s) => s.orgSlug === slug);

  if (known(orgHint)) return orgHint;
  const lastUsed = readLastActiveOrgSlug();
  if (known(lastUsed)) return lastUsed;
  if (known(defaultOrgSlug)) return defaultOrgSlug;
  return sessions[0]?.orgSlug ?? null;
}

function readLastActiveOrgSlug(): string | null {
  try {
    return localStorage.getItem(LAST_ACTIVE_ORG_KEY);
  } catch {
    return null;
  }
}

// Reads the active org's session, which AuthGuard fills from /_auth/me before the
// router mounts — so this answers with no fetch to await.
export function getActiveSession(): StoredSession | null {
  const orgId = getActiveOrgId();
  return orgId ? getSession(orgId) : null;
}

export { getActiveOrgId, getActiveOrgSlug } from 'auth/activeOrg';
